import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Loads /general-settings once per session (cached in module scope) and
 * returns a `maskPhone` helper that hides the last 4 digits when
 * `hide_phone_last_digits` is enabled.
 *
 * Usage:
 *   const { maskPhone, settings } = useGeneralSettings();
 *   <span>{maskPhone(phone)}</span>
 */
let cache = null;
let inflight = null;
const listeners = new Set();

function notify(value) {
  cache = value;
  listeners.forEach((l) => l(value));
}

export function useGeneralSettings() {
  const [settings, setSettings] = useState(cache || {});

  useEffect(() => {
    listeners.add(setSettings);
    if (cache) {
      setSettings(cache);
    } else {
      if (!inflight) {
        inflight = api.get("/general-settings")
          .then((r) => { notify(r.data || {}); return r.data; })
          .catch(() => { notify({}); return {}; })
          .finally(() => { inflight = null; });
      }
    }
    return () => { listeners.delete(setSettings); };
  }, []);

  const maskPhone = (p) => {
    if (!p) return "";
    if (!settings.hide_phone_last_digits) return p;
    return p.slice(0, Math.max(0, p.length - 4)) + "****";
  };

  /**
   * If `name` is empty or looks like a phone number (WhatsApp fallback when the
   * sender has no contact saved), returns the masked phone. Otherwise returns name.
   */
  const displayContactName = (name, phone) => {
    if (!name) return maskPhone(phone || "");
    const isPhoneLike = /^[\s+\-()]*\d[\d\s\-()]*$/.test(name.trim());
    if (isPhoneLike) return maskPhone(name);
    return name;
  };

  return { settings, maskPhone, displayContactName };
}

/** Invalidate the cache — call after updating general settings. */
export function refreshGeneralSettings() {
  cache = null;
  inflight = api.get("/general-settings")
    .then((r) => { notify(r.data || {}); return r.data; })
    .catch(() => { notify({}); return {}; })
    .finally(() => { inflight = null; });
}
